/**
 * 30-Night Strip. The companion to the lunar dial — a horizontal cadence of
 * the last 30 nights, each bar height = hours, color oklch fade by total.
 *
 * • Scroll-driven draw: each bar's scaleY animates from 0→1 as the strip
 *   crosses the viewport.
 * • Anchor-positioning tooltips per bar (with fallback for browsers without
 *   anchor-name support).
 * • :focus-visible + :hover state shows the per-bar label.
 *
 * Registers as <aramgah-nightly-bars>. Name retained for compat.
 */
import { store } from '../store.ts';
import type { SleepNight } from '../types.ts';
import { tierForHours, TIER_DISPLAY } from '../types.ts';

function colorFor(hours: number): string {
  // oklch fade from rose (poor) → moon-cream (gold-grade)
  if (hours >= 8.0)  return 'oklch(91% 0.04 80)';
  if (hours >= 7.0)  return 'oklch(86% 0.08 84)';
  if (hours >= 6.0)  return 'oklch(82% 0.02 250)';
  if (hours >= 5.0)  return 'oklch(72% 0.10 50)';
  return 'oklch(60% 0.18 25)';
}

function ddmm(date: string): string {
  const [y, m, d] = date.split('-');
  if (!y || !m || !d) return date;
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${parseInt(d, 10)} ${months[parseInt(m, 10) - 1] ?? ''}`;
}

export class AramgahNightlyBars extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub?: () => void;

  connectedCallback(): void {
    this.#unsub = store.subscribe((s) => {
      if (s.summary) this.#render(s.summary.nights);
    });
  }
  disconnectedCallback(): void { this.#unsub?.(); }

  #render(nights: SleepNight[]): void {
    if (!nights.length) { this.#shadow.innerHTML = '<style>:host{display:none;}</style>'; return; }
    const ordered = [...nights].sort((a, b) => a.date.localeCompare(b.date)).slice(-30);
    const maxHours = Math.max(...ordered.map((n) => n.duration_hours), 9);
    const minHours = Math.min(...ordered.map((n) => n.duration_hours));
    const avg = ordered.reduce((s, n) => s + n.duration_hours, 0) / ordered.length;

    const bars = ordered.map((n, i) => {
      const pct = Math.max(6, (n.duration_hours / maxHours) * 100);
      const color = colorFor(n.duration_hours);
      const tier = TIER_DISPLAY[tierForHours(n.duration_hours)].name;
      const delay = i * 18;
      return /* html */ `
<button
  type="button"
  class="bar"
  aria-label="${ddmm(n.date)} · ${n.duration_hours.toFixed(1)} hours · ${tier}"
  data-date="${ddmm(n.date)}"
  data-hours="${n.duration_hours.toFixed(1)}"
  data-tier="${tier}"
  style="--h:${pct}%; --c:${color}; --anchor-name:--bar-${i}; --delay:${delay}ms;"
>
  <span class="tip" aria-hidden="true">
    <span class="tip-date">${ddmm(n.date)}</span>
    <span class="tip-hours">${n.duration_hours.toFixed(1)}h</span>
    <span class="tip-tier">${tier}</span>
  </span>
</button>`;
    }).join('');

    this.#shadow.innerHTML = /* html */ `
<style>
  :host {
    display: block;
    border: 1px solid var(--rule);
    border-radius: 12px;
    padding: 22px 22px 26px;
    background:
      linear-gradient(180deg, oklch(16% 0.05 270 / 0.7), oklch(13% 0.04 268 / 0.7)),
      var(--paper-up);
    container-type: inline-size;
    position: relative;
    overflow: hidden;
    --tip-h: 70px;
  }
  :host::before {
    content: '';
    position: absolute; inset: 0;
    background: radial-gradient(ellipse 100% 40% at 50% 100%, oklch(82% 0.04 250 / 0.05), transparent 70%);
    pointer-events: none;
  }
  .head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 10px;
    margin-bottom: 16px;
    flex-wrap: wrap;
  }
  .head .eyebrow {
    font-family: var(--mono);
    font-size: 10px;
    letter-spacing: 0.4em;
    text-transform: uppercase;
    color: var(--ink-faint);
  }
  .head .sub {
    font-family: var(--display);
    font-style: italic;
    font-size: 14px;
    color: var(--ink-dim);
  }
  .stage {
    position: relative;
    display: grid;
    grid-template-columns: repeat(${ordered.length}, 1fr);
    gap: 3px;
    align-items: end;
    height: 116px;
    padding-top: var(--tip-h);
  }
  .bar {
    height: var(--h);
    min-height: 4px;
    background: var(--c);
    border-radius: 2px 2px 0 0;
    cursor: pointer;
    position: relative;
    transform-origin: bottom;
    transition: filter 200ms var(--ease-suite);
    /* scroll-driven grow + per-bar stagger */
    animation: bar-rise 720ms var(--ease-suite) both;
    animation-delay: var(--delay);
    animation-timeline: view();
    animation-range: cover 5% cover 50%;
    anchor-name: var(--anchor-name);
  }
  .bar:hover, .bar:focus-visible {
    filter: brightness(1.2) saturate(1.05);
    z-index: 2;
  }
  .bar:hover .tip, .bar:focus-visible .tip { opacity: 1; transform: translate(-50%, 0); }

  .tip {
    position: absolute;
    bottom: calc(100% + 10px);
    left: 50%;
    transform: translate(-50%, 6px);
    display: grid;
    gap: 2px;
    padding: 10px 12px;
    background: var(--paper-card);
    border: 1px solid var(--rule-strong);
    border-radius: 6px;
    min-width: 84px;
    text-align: center;
    opacity: 0;
    transition: opacity 200ms var(--ease-suite), transform 200ms var(--ease-suite);
    pointer-events: none;
    z-index: 5;
    box-shadow: 0 12px 30px oklch(0% 0 0 / 0.4);
  }
  .tip::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: var(--paper-card);
  }
  .tip-date {
    font-family: var(--mono);
    font-size: 9px;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    color: var(--ink-faint);
  }
  .tip-hours {
    font-family: var(--display);
    font-style: italic;
    font-size: 22px;
    font-weight: 700;
    color: var(--moon-cream);
    font-feature-settings: 'tnum' 1, 'ss01' 1;
  }
  .tip-tier {
    font-family: var(--display);
    font-style: italic;
    font-size: 11px;
    color: var(--ink-dim);
  }

  /* Anchor-positioning enhancement (Chrome 125+) — keeps tooltips inside viewport */
  @supports (anchor-name: --x) {
    .tip {
      bottom: auto;
      left: auto;
      transform: none;
      top: anchor(top);
      left: anchor(center);
      translate: -50% calc(-100% - 8px);
    }
    .bar:hover .tip, .bar:focus-visible .tip {
      translate: -50% calc(-100% - 14px);
    }
  }

  .scale {
    margin-top: 14px;
    display: flex;
    justify-content: space-between;
    font-family: var(--mono);
    font-size: 9px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--ink-faint);
  }
  .legend {
    margin-top: 16px;
    padding-top: 14px;
    border-top: 1px dashed var(--rule);
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
    font-family: var(--mono);
    font-size: 9px;
    letter-spacing: 0.25em;
    text-transform: uppercase;
    color: var(--ink-faint);
  }
  .legend .swatch {
    display: inline-block;
    width: 9px; height: 9px;
    border-radius: 2px;
    margin-right: 5px;
    vertical-align: middle;
    box-shadow: 0 0 6px currentColor;
  }
  .marginalia {
    margin-top: 18px;
    padding-top: 14px;
    border-top: 1px solid var(--rule);
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 18px;
  }
  @container (max-width: 420px) {
    .marginalia { grid-template-columns: 1fr; }
  }
  .marginalia .cell .k {
    font-family: var(--mono);
    font-size: 9px;
    letter-spacing: 0.4em;
    text-transform: uppercase;
    color: var(--ink-faint);
  }
  .marginalia .cell .v {
    font-family: var(--display);
    font-style: italic;
    font-size: 22px;
    font-weight: 600;
    color: var(--moon-cream);
    margin-top: 4px;
    font-feature-settings: 'tnum' 1, 'ss01' 1;
  }
  @keyframes bar-rise {
    from { transform: scaleY(0); opacity: 0; }
    to   { transform: scaleY(1); opacity: 1; }
  }
  @media (prefers-reduced-motion: reduce) {
    .bar { animation: none; }
  }
  @supports not (animation-timeline: view()) {
    .bar {
      animation: bar-rise 720ms var(--ease-suite) both;
    }
  }
</style>
<div class="head">
  <div class="eyebrow">30 nights · ka silsila</div>
  <div class="sub">avg ${avg.toFixed(1)}h · range ${minHours.toFixed(1)}–${maxHours.toFixed(1)}h</div>
</div>
<div class="stage">${bars}</div>
<div class="scale"><span>30 nights ago</span><span>last night →</span></div>
<div class="legend">
  <span><span class="swatch" style="background:oklch(60% 0.18 25); color:oklch(60% 0.18 25)"></span>under 5h</span>
  <span><span class="swatch" style="background:oklch(72% 0.10 50); color:oklch(72% 0.10 50)"></span>5–6h</span>
  <span><span class="swatch" style="background:oklch(82% 0.02 250); color:oklch(82% 0.02 250)"></span>6–7h</span>
  <span><span class="swatch" style="background:oklch(86% 0.08 84); color:oklch(86% 0.08 84)"></span>7–8h</span>
  <span><span class="swatch" style="background:oklch(91% 0.04 80); color:oklch(91% 0.04 80)"></span>8h +</span>
</div>
<div class="marginalia">
  <div class="cell"><div class="k">Longest night</div><div class="v">${maxHours.toFixed(1)}h</div></div>
  <div class="cell" style="text-align:right;"><div class="k">Shortest night</div><div class="v">${minHours.toFixed(1)}h</div></div>
</div>
`;
  }
}

customElements.define('aramgah-nightly-bars', AramgahNightlyBars);
